// Format predicates read off league settings. One definition, imported
// everywhere, because the thing this file exists to prevent is exactly what
// happened before it: nine separate copies of `(positions.SFLEX || 0) > 0`
// standing in for "this league values QBs like a premium position", and a
// 2QB league — which has that property and no SFLEX slot — falling through
// every one of them onto the 1QB curve.
//
// Reported from the wild (r/fantasyfootball thread 1vnrwn3, launch night):
// a 2QB/1WR/1RB/1TE/3FLEX room priced Allen at $38 and Hurts at $8. The
// Aug-13 corpus has 41 true-2QB drafts and those ROOMS cleared Allen in the
// low-to-mid $50s — so the bidding was already right (demand comes from
// empty roster slots, and a 2QB roster has two of them). Only the sticker
// was wrong.
//
// SCOPE — this is a DEMAND predicate, not a slot predicate. Use it where the
// question is "does this league bid QBs up?" (pricing curves, drafted-pool
// fractions, QB strategy assignment, bench demand). Do NOT use it where the
// question is "is there an SFLEX slot on the roster?" — flex eligibility,
// SFLEX column rendering and the settings preset matchers all still want the
// literal SFLEX count and are correct as they are.

/**
 * True when a league's roster forces QB demand past the single-starter norm:
 * either a superflex slot, or two-or-more mandatory QB starters.
 *
 * Superflex leagues answer true on the first clause, so every site swapped to
 * this predicate is byte-identical for SFLEX>0 and for the 1QB default — only
 * QB>=2 rosters change. Combination rosters seen in the corpus (2QB+SFLEX,
 * double-SFLEX, 0QB+2SFLEX) fall out of the two clauses without a special case.
 *
 * @param {object} positions - settings.positions
 * @returns {boolean}
 */
export function isQbPremiumFormat(positions) {
  const pos = positions || {};
  return (pos.SFLEX || 0) > 0 || (pos.QB || 0) >= 2;
}

/**
 * The format's NAME, for labelling — analytics dimensions, header chips.
 *
 * Three labels, not four. A 2QB+SFLEX roster is 'sflex': it has a superflex
 * slot, the engine treats it as superflex everywhere, and a fourth label would
 * split that population off from the arm every measurement was taken on. Its
 * QB count is still recoverable — the analytics payload carries `qb_slots` and
 * `sflex_slots` alongside this — so nothing is lost by not naming it.
 *
 * Ordered to match `isQbPremiumFormat`'s clauses on purpose: 'sflex' and '2qb'
 * are exactly the two cases that predicate answers true for, so the label and
 * the behaviour can never disagree about which rooms are QB-premium.
 *
 * @param {object} positions - settings.positions
 * @returns {'1qb'|'sflex'|'2qb'}
 */
export function leagueFormatLabel(positions) {
  const pos = positions || {};
  if ((pos.SFLEX || 0) > 0) return 'sflex';
  if ((pos.QB || 0) >= 2) return '2qb';
  return '1qb';
}
